import React from 'react'
import Link from 'next/link'

/**
 * Women's-health programming detail for the female trainer page.
 *
 * The female and freelance pages previously shared ~93 of 130 long sentences
 * because both were built almost entirely from the same components. This is
 * page-specific substance: what a female trainer actually changes about the
 * programming, in the language people search for.
 *
 * Every claim here maps to something StringsSG genuinely offers - the linked
 * prenatal/postpartum page exists, and the roster includes trainers with these
 * specialisms. Keep it that way.
 */

const focusAreas = [
    {
        title: 'Prenatal training',
        body: 'Programming that adapts each trimester: load managed as the bump grows, positions changed as lying flat becomes uncomfortable, and core work shifted away from anything that pushes the abdominal wall outward.',
    },
    {
        title: 'Postpartum recovery',
        body: 'Rebuilding from the inside out after birth. Breathing and deep core reconnection first, then pelvic floor work, then progressive loading - rather than returning straight to the routine you had before.',
    },
    {
        title: 'Diastasis recti',
        body: 'Abdominal separation needs specific progressions and a few exercises avoided entirely. A trainer who knows what to look for will screen for it before writing your programme.',
    },
    {
        title: 'Pelvic floor awareness',
        body: 'Leaking during jumps or heavy lifts is common and is not something you simply put up with. Sessions can be built around building pressure management instead of working around the symptom.',
    },
    {
        title: 'PCOS and hormonal health',
        body: 'Strength training and steady, sustainable habits tend to work better than aggressive calorie restriction. Programming focuses on consistency and recovery rather than chasing a fast deficit.',
    },
    {
        title: 'Perimenopause and menopause',
        body: 'Bone density, lean mass and joint comfort all become higher priorities. Resistance training moves to the centre of the plan, with recovery and sleep treated as part of the programme.',
    },
    {
        title: 'Cycle-aware programming',
        body: 'Energy, strength and recovery shift across the month. A trainer who tracks this with you can put the heavier sessions where they will actually go well, rather than fighting your cycle every week.',
    },
    {
        title: 'Strength without bulk',
        body: 'The most common concern we hear, and the easiest to address: building visible strength and definition takes years of deliberate surplus. Training hard will not make you bulky by accident.',
    },
]

export const FemaleTrainingFocus = () => (
    <section className='relative bg-slate-900 py-12 md:py-16 overflow-hidden'>
        <div className='absolute top-0 right-0 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl pointer-events-none' />
        <div className='absolute bottom-0 left-0 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl pointer-events-none' />

        <div className='relative z-10 max-w-5xl mx-auto px-4'>
            <div className='text-center mb-8'>
                <div className='inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 border border-white/10'>
                    <span className='text-sm'>🩺</span>
                    <span className='text-xs font-medium text-white/70'>Women&apos;s health</span>
                </div>
                <h2 className='text-2xl md:text-3xl font-extrabold text-white tracking-tight'>
                    What Changes When You Train With a Female Personal Trainer
                </h2>
                <p className='text-sm md:text-base text-white/60 mt-3 max-w-2xl mx-auto leading-relaxed'>
                    Asking for a female trainer is usually about more than comfort. These are the areas
                    our female trainers in Singapore are most often asked to programme around.
                </p>
            </div>

            <div className='grid sm:grid-cols-2 gap-4'>
                {focusAreas.map((area) => (
                    <div key={area.title}
                        className='bg-white/5 border border-white/10 rounded-2xl p-4 hover:border-orange-400/30 transition-colors'>
                        <h3 className='text-base font-bold text-white mb-1.5'>{area.title}</h3>
                        <p className='text-sm leading-relaxed text-white/65'>{area.body}</p>
                    </div>
                ))}
            </div>

            <p className='text-sm text-white/60 mt-8 text-center leading-relaxed max-w-3xl mx-auto'>
                Training through pregnancy or recovering afterwards?{' '}
                <Link href='/sg/personal-trainer/prenatal-postpartum-training' prefetch={false}
                    className='text-orange-400 hover:text-orange-300 underline'>
                    See our prenatal and postpartum training in Singapore
                </Link>
                , or bring it up during your free consultation and we will match you with a trainer who
                works in that area.
            </p>
        </div>
    </section>
)
